import {
    FC,
    useMemo,
    useState,
    useCallback,
    useRef,
    useEffect,
    useLayoutEffect
} from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import s from './Searchbar.module.scss'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import SearchAutocomplete from '../SearchAutocomplete'
import debounce from 'lodash.debounce'
import { Barcode, Search as SearchIcon } from '@components/icons'
import { useUI } from '@corratech/pylot-ui/context'
import { useOnClickOutside } from '@lib/hooks/useOnClickOutside'
import { LoadingDots } from '@corratech/pylot-ui'
import { LoadingIndicator } from '@components/LoadingIndicator'
import dynamic from 'next/dynamic'
import type { DynamicOptionsLoadingProps } from 'next/dynamic'
import searchRedirectsJson from '@lib/searchRedirects.json'
import { isMobileScreen, useIsMobileScreen } from '@lib/utils/mediaQuery'
import {
    VISUAL_SEARCH_IMAGE_SESSION_KEY,
    VISUAL_SEARCH_OBJECT_LABEL_SESSION_KEY,
    VISUAL_SEARCH_REQUEST_QUERY_KEY,
    clearVisualSearchSession,
    isVisualSearchQuery
} from '@pylot-data/hooks/search/visualSearchSession'
import { prepareVisualSearchImage } from '@pylot-data/hooks/search/utils/prepareVisualSearchImage'
import { openImageConsentModal } from '@components/common/ImageConsentModal/imageConsentAlert'
import { useVisualSearchEnabled } from './graphql/useVisualSearchEnabled'

const ModalLoading: FC = () => <LoadingIndicator />
ModalLoading.displayName = 'ModalLoading'

const ScannerLoading: FC = () => <LoadingDots />
ScannerLoading.displayName = 'ScannerLoading'

const renderModalLoading = (_props: DynamicOptionsLoadingProps) => (
    <ModalLoading />
)
renderModalLoading.displayName = 'renderModalLoading'

const renderScannerLoading = (_props: DynamicOptionsLoadingProps) => (
    <ScannerLoading />
)
renderScannerLoading.displayName = 'renderScannerLoading'

const Modal = dynamic(() => import('@corratech/pylot-ui/src/Modal'), {
    loading: renderModalLoading
})

const Scanner = dynamic(() => import('@components/BarcodeReader/Scanner'), {
    loading: renderScannerLoading
})

const searchPageURL = '/search'

interface Props {
    className?: string
    id?: string
    variant?: 'card' | 'header'
    onInputFocus?: () => void
    onInputBlur?: () => void
    onInputChange?: (value: string) => void
    onCloseAutocomplete?: () => void
    closeAutocompleteRef?: React.MutableRefObject<(() => void) | null>
}

type LocalRedirects = {
    [k: string]:
        | {
              destination: string
              permanent: boolean
              isUrl?: boolean
          }
        | undefined
}

const getQueryParam = (q: string | string[] | undefined): string => {
    if (typeof q === 'string') return q
    if (Array.isArray(q) && typeof q[0] === 'string') return q[0]
    return ''
}

// En Android, input[accept=image/*] sin capture abre solo la galería.
const isAndroidDevice = (): boolean =>
    typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)

const activateFileInput = (input: HTMLInputElement | null): void => {
    if (!input) return
    input.value = ''
    input.click()
}

const Searchbar: FC<Props> = ({
    className,
    id = 'search',
    variant,
    onInputFocus,
    onInputBlur,
    onInputChange,
    onCloseAutocomplete,
    closeAutocompleteRef
}) => {
    const router = useRouter()
    const { t } = useTranslation(['common'])
    const [searchTerm, setSearchTerm] = useState<string>(() =>
        getQueryParam(router.query.q)
    )
    // Debounced copy used only for autocomplete API suggestions
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
    const [showAutoComplete, setShowAutoComplete] = useState<boolean>(false)
    const [panelOpen, setPanelOpen] = useState(false)
    const [cardTop, setCardTop] = useState(0)
    const [startToScan, setStartToScan] = useState(false)
    const { closeSearch } = useUI()
    // false en SSR y primer paint → evita mismatch del placeholder
    const isMobile = useIsMobileScreen()
    const [supportsUserMedia, setSupportsUserMedia] = useState(false)
    const visualSearchInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)
    const [isPreparingVisualSearch, setIsPreparingVisualSearch] =
        useState(false)
    const [visualSearchPortalReady, setVisualSearchPortalReady] =
        useState(false)
    const [androidSourceOpen, setAndroidSourceOpen] = useState(false)
    // Solo true cuando Magento confirma ON (undefined/false = no montar cámara).
    const isVisualSearchEnabled = useVisualSearchEnabled() === true

    useEffect(() => {
        setVisualSearchPortalReady(true)
    }, [])

    const resetVisualSearchInputs = useCallback(() => {
        if (visualSearchInputRef.current) {
            visualSearchInputRef.current.value = ''
        }
        if (cameraInputRef.current) {
            cameraInputRef.current.value = ''
        }
    }, [])

    const openVisualSearchSource = useCallback(() => {
        if (isPreparingVisualSearch) return
        // iOS muestra cámara o galería en el selector nativo. Android no:
        // sin capture abre solo la galería, y con capture abre solo la cámara.
        if (isAndroidDevice()) {
            setAndroidSourceOpen(true)
            return
        }
        activateFileInput(visualSearchInputRef.current)
    }, [isPreparingVisualSearch])

    const openVisualSearchAfterConsent = useCallback(() => {
        openVisualSearchSource()
    }, [openVisualSearchSource])

    useEffect(() => {
        if (!isVisualSearchEnabled) return
        if (router.query.imageConsent === '1') {
            openImageConsentModal(openVisualSearchAfterConsent)
        }
    }, [
        isVisualSearchEnabled,
        router.query.imageConsent,
        openVisualSearchAfterConsent
    ])

    useEffect(() => {
        setSupportsUserMedia(
            !!(
                window.navigator.mediaDevices &&
                typeof window.navigator.mediaDevices.getUserMedia === 'function'
            )
        )
    }, [])

    useEffect(() => {
        if (showAutoComplete) {
            const scrollY = window.scrollY
            document.body.style.position = 'fixed'
            document.body.style.top = `-${scrollY}px`
            document.body.style.width = '100%'
            document.body.style.overflow = 'hidden'
        } else {
            const scrollY = document.body.style.top
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            document.body.style.overflow = ''
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1)
            }
        }
        return () => {
            const scrollY = document.body.style.top
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            document.body.style.overflow = ''
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1)
            }
        }
    }, [showAutoComplete])

    useEffect(() => {
        const launcher = document.getElementById('launcher')
        if (!launcher) return

        const previousDisplay = launcher.style.display

        if (showAutoComplete) {
            launcher.style.display = 'none'
        } else {
            launcher.style.display = previousDisplay || ''
        }

        return () => {
            if (launcher) {
                launcher.style.display = previousDisplay || ''
            }
        }
    }, [showAutoComplete])

    const isAutocompleteVisible = showAutoComplete

    const debounceSearchTerm = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedSearchTerm(value)
                if (value.length > 2) setShowAutoComplete(true)
            }, 400),
        []
    )

    useEffect(() => {
        return () => {
            debounceSearchTerm.cancel()
        }
    }, [debounceSearchTerm])

    const searchBarRef = useRef<HTMLDivElement>(null)
    const autocompleteRef = useRef<HTMLDivElement>(null)
    const cardRef = useRef<HTMLDivElement>(null)
    const userInitiatedFocus = useRef(false)
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>

    useEffect(() => {
        if (!panelOpen) return
        ref.current?.focus()
    }, [panelOpen])

    useLayoutEffect(() => {
        if (!panelOpen) return
        const update = () => {
            const nav = document.getElementById('navbar')
            const box = nav?.parentElement ?? nav
            if (box) setCardTop(box.getBoundingClientRect().bottom)
        }
        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [panelOpen])

    useEffect(() => {
        if (ref.current && !isMobileScreen()) {
            ref.current.focus()
        }
    }, [])

    /** Hides panel + cancels pending debounce so body unlock sticks after navigate */
    const hideAutocomplete = useCallback(() => {
        debounceSearchTerm.cancel()
        setShowAutoComplete(false)
    }, [debounceSearchTerm])

    const closeAutocomplete = useCallback(() => {
        hideAutocomplete()
        setPanelOpen(false)
        setSearchTerm('')
        setDebouncedSearchTerm('')
        onInputChange?.('')
        onCloseAutocomplete?.()
    }, [hideAutocomplete, onInputChange, onCloseAutocomplete])

    useEffect(() => {
        router.prefetch(searchPageURL)
        const onRouteChangeStart = () => {
            closeSearch()
            // Unlock body scroll even if a pending debounce would re-open autocomplete
            hideAutocomplete()
        }
        router.events.on('routeChangeStart', onRouteChangeStart)
        return () => {
            router.events.off('routeChangeStart', onRouteChangeStart)
        }
    }, [router, closeSearch, hideAutocomplete])

    useEffect(() => {
        if (closeAutocompleteRef) {
            closeAutocompleteRef.current = closeAutocomplete
        }
    }, [closeAutocomplete, closeAutocompleteRef])

    useOnClickOutside(searchBarRef, (event) => {
        const target = event.target as Node
        if (cardRef.current?.contains(target)) return
        if (isMobile && autocompleteRef.current?.contains(target)) return
        if (showAutoComplete || panelOpen) {
            closeAutocomplete()
        }
    })

    const handleBarcodeDetected = (result: string) => {
        setStartToScan(false)
        router.push(`/search?q=${result}`)
    }

    const openBarcodeScanner = () => {
        ref.current?.blur()
        setStartToScan(true)
        closeAutocomplete()
    }

    const handleSearch = () => {
        if (searchTerm.length > 2) {
            hideAutocomplete()
            setPanelOpen(false)
            // Si venimos de búsqueda visual, no usar shallow: GSSP debe
            // recalcular isVisualSearch=false. También limpiar la imagen en session.
            const leavingVisualSearch = isVisualSearchQuery(
                router.query.visual_search
            )
            if (leavingVisualSearch) {
                clearVisualSearchSession()
            }
            const useShallow =
                router.pathname === searchPageURL && !leavingVisualSearch
            const searchRedirects = (searchRedirectsJson as LocalRedirects)[
                searchTerm
            ]
            if (searchRedirects) {
                if (!searchRedirects.isUrl) {
                    router.push(
                        {
                            pathname: searchPageURL,
                            query: { q: searchRedirects.destination }
                        },
                        undefined,
                        { shallow: useShallow }
                    )
                } else {
                    router.push(searchRedirects.destination)
                }
            } else {
                router.push(
                    {
                        pathname: searchPageURL,
                        query: { q: searchTerm }
                    },
                    undefined,
                    { shallow: useShallow }
                )
            }
        }
    }

    const handleVisualSearch = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0]
        if (!file) return

        setIsPreparingVisualSearch(true)
        try {
            const prepared = await prepareVisualSearchImage(file)

            try {
                sessionStorage.setItem(
                    VISUAL_SEARCH_IMAGE_SESSION_KEY,
                    prepared.imageB64
                )
                if (prepared.objectLabel) {
                    sessionStorage.setItem(
                        VISUAL_SEARCH_OBJECT_LABEL_SESSION_KEY,
                        prepared.objectLabel
                    )
                } else {
                    sessionStorage.removeItem(
                        VISUAL_SEARCH_OBJECT_LABEL_SESSION_KEY
                    )
                }
            } catch (storageError) {
                console.error(
                    'No se pudo guardar la imagen para búsqueda visual:',
                    storageError
                )
                resetVisualSearchInputs()
                return
            }

            await router.push({
                pathname: searchPageURL,
                query: {
                    visual_search: '1',
                    [VISUAL_SEARCH_REQUEST_QUERY_KEY]: Date.now().toString()
                }
            })
        } catch (error) {
            console.error('Error en búsqueda visual:', error)
        } finally {
            setIsPreparingVisualSearch(false)
            setAndroidSourceOpen(false)
            resetVisualSearchInputs()
        }
    }

    const handleInputChange = (value: string) => {
        setSearchTerm(value)
        onInputChange?.(value)
        debounceSearchTerm(value)
    }

    const desktopHeaderPanel = !isMobile

    if (variant === 'header') {
        return (
            <div className={cn('flex', className)} ref={searchBarRef}>
                <button
                    type="button"
                    className={s.searchTrigger}
                    aria-label={t('Search', { defaultValue: 'Buscar' })}
                    aria-expanded={panelOpen}
                    data-search-open={panelOpen ? '' : undefined}
                    onClick={() => {
                        if (panelOpen) {
                            closeAutocomplete()
                            return
                        }
                        setPanelOpen(true)
                        setShowAutoComplete(true)
                    }}
                >
                    <SearchIcon />
                </button>
                {panelOpen &&
                    createPortal(
                        <div
                            className={
                                desktopHeaderPanel ? s.searchCard : s.searchSheet
                            }
                            ref={cardRef}
                            style={{ top: cardTop }}
                        >
                            <div
                                className={
                                    desktopHeaderPanel
                                        ? s.searchCardInner
                                        : undefined
                                }
                            >
                            <div
                                className={
                                    desktopHeaderPanel
                                        ? s.searchCardRow
                                        : s.searchSheetRow
                                }
                            >
                                <div
                                    className={
                                        desktopHeaderPanel
                                            ? s.searchCardField
                                            : s.searchSheetField
                                    }
                                >
                                    {!desktopHeaderPanel && (
                                        <button
                                            type="button"
                                            aria-label={t('Write your search')}
                                            className={s.searchSheetLens}
                                            onClick={handleSearch}
                                        >
                                            <SearchIcon />
                                        </button>
                                    )}
                                    <label className="sr-only" htmlFor={id}>
                                        {t('Search products placeholder', {
                                            defaultValue: 'Buscar productos'
                                        })}
                                    </label>
                                    <input
                                        id={id}
                                        ref={ref}
                                        className={
                                            desktopHeaderPanel
                                                ? s.searchCardInput
                                                : s.searchSheetInput
                                        }
                                        onChange={(e) =>
                                            handleInputChange(e.target.value)
                                        }
                                        onMouseDown={() => {
                                            userInitiatedFocus.current = true
                                        }}
                                        onTouchStart={() => {
                                            userInitiatedFocus.current = true
                                        }}
                                        onFocus={() => {
                                            if (userInitiatedFocus.current) {
                                                setShowAutoComplete(true)
                                            }
                                            userInitiatedFocus.current = false
                                        }}
                                        onClick={() => setShowAutoComplete(true)}
                                        placeholder={t(
                                            'Search products placeholder',
                                            {
                                                defaultValue: 'Buscar productos'
                                            }
                                        )}
                                        autoComplete="off"
                                        value={searchTerm}
                                        onKeyUp={(e) => {
                                            if (e.key === 'Enter') handleSearch()
                                        }}
                                    />
                                    {isVisualSearchEnabled && (
                                        <button
                                            type="button"
                                            aria-label={t('Visual Search')}
                                            aria-busy={isPreparingVisualSearch}
                                            disabled={isPreparingVisualSearch}
                                            className={s.searchSheetCamera}
                                            onClick={() => {
                                                if (isPreparingVisualSearch) {
                                                    return
                                                }
                                                openImageConsentModal(
                                                    openVisualSearchAfterConsent
                                                )
                                            }}
                                        >
                                            {isPreparingVisualSearch ? (
                                                <span
                                                    className={
                                                        s['camera-icon-svg']
                                                    }
                                                >
                                                    <LoadingDots />
                                                </span>
                                            ) : (
                                                <img
                                                    src="/camera.svg"
                                                    alt=""
                                                    className={
                                                        s['camera-icon-svg']
                                                    }
                                                />
                                            )}
                                        </button>
                                    )}
                                    {!desktopHeaderPanel &&
                                        supportsUserMedia && (
                                        <button
                                            type="button"
                                            aria-label={t('escanear producto')}
                                            className={s.searchSheetBarcode}
                                            onClick={openBarcodeScanner}
                                        >
                                            <Barcode />
                                        </button>
                                    )}
                                    {desktopHeaderPanel && (
                                        <button
                                            type="button"
                                            aria-label={t('Write your search')}
                                            className={s.searchCardLens}
                                            onClick={handleSearch}
                                        >
                                            <SearchIcon />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    aria-label={t('Close panel')}
                                    className={s.searchCardClose}
                                    onClick={closeAutocomplete}
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        aria-hidden
                                    >
                                        <path
                                            d="M1 11L11 1M11 11L1 1"
                                            stroke="#696158"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                            {isAutocompleteVisible && (
                                <div ref={autocompleteRef}>
                                    <SearchAutocomplete
                                        searchTerm={debouncedSearchTerm}
                                        searchPageURL={searchPageURL}
                                        closeAutocomplete={closeAutocomplete}
                                        onSearchSubmit={handleSearch}
                                        layout={
                                            desktopHeaderPanel ? 'card' : 'sheet'
                                        }
                                    />
                                </div>
                            )}
                            </div>
                        </div>,
                        document.body
                    )}
                {isVisualSearchEnabled &&
                    visualSearchPortalReady &&
                    createPortal(
                        <>
                            <input
                                type="file"
                                ref={visualSearchInputRef}
                                className={s['visual-search-input']}
                                accept="image/*"
                                aria-hidden
                                tabIndex={-1}
                                onChange={handleVisualSearch}
                            />
                            <input
                                type="file"
                                ref={cameraInputRef}
                                className={s['visual-search-input']}
                                accept="image/*"
                                capture="environment"
                                aria-hidden
                                tabIndex={-1}
                                onChange={handleVisualSearch}
                            />
                        </>,
                        document.body
                    )}
                {isVisualSearchEnabled &&
                    androidSourceOpen &&
                    createPortal(
                        <div className={s['visual-source']}>
                            <button
                                type="button"
                                className={s['visual-source-backdrop']}
                                aria-label={t('Close')}
                                onClick={() => setAndroidSourceOpen(false)}
                            />
                            <div
                                className={s['visual-source-sheet']}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby={`${id}-visual-source-title`}
                            >
                                <p
                                    id={`${id}-visual-source-title`}
                                    className={s['visual-source-title']}
                                >
                                    {t('search|Visual search source title', {
                                        defaultValue: 'Elige una opción'
                                    })}
                                </p>
                                <button
                                    type="button"
                                    className={s['visual-source-option']}
                                    onClick={() => {
                                        activateFileInput(cameraInputRef.current)
                                        setAndroidSourceOpen(false)
                                    }}
                                >
                                    {t('search|Visual search take photo', {
                                        defaultValue: 'Tomar foto'
                                    })}
                                </button>
                                <button
                                    type="button"
                                    className={s['visual-source-option']}
                                    onClick={() => {
                                        activateFileInput(
                                            visualSearchInputRef.current
                                        )
                                        setAndroidSourceOpen(false)
                                    }}
                                >
                                    {t('search|Visual search choose gallery', {
                                        defaultValue: 'Elegir de la galería'
                                    })}
                                </button>
                                <button
                                    type="button"
                                    className={s['visual-source-cancel']}
                                    onClick={() => setAndroidSourceOpen(false)}
                                >
                                    {t('search|Visual search source cancel', {
                                        defaultValue: 'Cancelar'
                                    })}
                                </button>
                            </div>
                        </div>,
                        document.body
                    )}
                {startToScan &&
                    createPortal(
                        <Modal
                            onClose={() => setStartToScan(false)}
                            open={startToScan}
                            className="scanner-modal"
                        >
                            <Scanner
                                onBarcodeDetected={handleBarcodeDetected}
                                startToScan={startToScan}
                            />
                        </Modal>,
                        document.body
                    )}
            </div>
        )
    }

    return (
        <div className="flex">
            <div
                className={cn(s['searchbar-main'], className)}
                ref={searchBarRef}
            >
                <label className="sr-only" htmlFor={id}>
                    {isMobile
                        ? t('Write your search mobile')
                        : t('Write your search')}
                </label>
                <button
                    aria-label={t('Write your search')}
                    className={cn(
                        s['lens-icon-container'],
                        !isVisualSearchEnabled && s['lens-icon-container--solo']
                    )}
                    onClick={handleSearch}
                >
                    <SearchIcon />
                </button>
                <input
                    id={id}
                    ref={ref}
                    className={cn(
                        s.input,
                        !isVisualSearchEnabled && s['input--no-camera']
                    )}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onMouseDown={() => {
                        userInitiatedFocus.current = true
                    }}
                    onTouchStart={() => {
                        userInitiatedFocus.current = true
                    }}
                    onFocus={() => {
                        if (userInitiatedFocus.current) {
                            setShowAutoComplete(true)
                        }
                        userInitiatedFocus.current = false
                        onInputFocus?.()
                    }}
                    onBlur={(e) => {
                        if (isMobile && isAutocompleteVisible) {
                            const relatedTarget = e.relatedTarget as Node | null
                            if (relatedTarget != null) {
                                setTimeout(() => {
                                    const activeElement = document.activeElement
                                    const isClickingAutocomplete =
                                        autocompleteRef.current?.contains(
                                            relatedTarget
                                        ) ||
                                        autocompleteRef.current?.contains(
                                            activeElement as Node
                                        )
                                    if (!isClickingAutocomplete) {
                                        closeAutocomplete()
                                    }
                                }, 150)
                            }
                        }
                        onInputBlur?.()
                    }}
                    onClick={() => {
                        setShowAutoComplete(true)
                    }}
                    placeholder={
                        isMobile
                            ? t('Write your search mobile')
                            : t('Write your search')
                    }
                    autoComplete="off"
                    value={searchTerm}
                    onKeyUp={(e) => {
                        if (e.key === 'Enter') handleSearch()
                    }}
                />
                {isVisualSearchEnabled && (
                    <button
                        type="button"
                        aria-label={t('Visual Search')}
                        aria-busy={isPreparingVisualSearch}
                        disabled={isPreparingVisualSearch}
                        className={cn(s['camera-button'], 'focus:outline-none')}
                        onClick={() => {
                            if (isPreparingVisualSearch) return
                            openImageConsentModal(openVisualSearchAfterConsent)
                        }}
                    >
                        {isPreparingVisualSearch ? (
                            <span
                                className={s['camera-icon-svg']}
                                title={t('Visual search preparing')}
                            >
                                <LoadingDots />
                            </span>
                        ) : (
                            <img
                                src="/camera.svg"
                                alt={t('Visual Search')}
                                className={s['camera-icon-svg']}
                                style={{ width: '28px', height: '28px' }}
                            />
                        )}
                    </button>
                )}
                {isVisualSearchEnabled &&
                    visualSearchPortalReady &&
                    createPortal(
                        <>
                            <input
                                type="file"
                                ref={visualSearchInputRef}
                                className={s['visual-search-input']}
                                accept="image/*"
                                aria-hidden
                                tabIndex={-1}
                                onChange={handleVisualSearch}
                            />
                            <input
                                type="file"
                                ref={cameraInputRef}
                                className={s['visual-search-input']}
                                accept="image/*"
                                capture="environment"
                                aria-hidden
                                tabIndex={-1}
                                onChange={handleVisualSearch}
                            />
                        </>,
                        document.body
                    )}
                {isVisualSearchEnabled &&
                    androidSourceOpen &&
                    createPortal(
                        <div className={s['visual-source']}>
                            <button
                                type="button"
                                className={s['visual-source-backdrop']}
                                aria-label={t('Close')}
                                onClick={() => setAndroidSourceOpen(false)}
                            />
                            <div
                                className={s['visual-source-sheet']}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby={`${id}-visual-source-title`}
                            >
                                <p
                                    id={`${id}-visual-source-title`}
                                    className={s['visual-source-title']}
                                >
                                    {t('search|Visual search source title', {
                                        defaultValue: 'Elige una opción'
                                    })}
                                </p>
                                <button
                                    type="button"
                                    className={s['visual-source-option']}
                                    onClick={() => {
                                        activateFileInput(
                                            cameraInputRef.current
                                        )
                                        setAndroidSourceOpen(false)
                                    }}
                                >
                                    {t('search|Visual search take photo', {
                                        defaultValue: 'Tomar foto'
                                    })}
                                </button>
                                <button
                                    type="button"
                                    className={s['visual-source-option']}
                                    onClick={() => {
                                        activateFileInput(
                                            visualSearchInputRef.current
                                        )
                                        setAndroidSourceOpen(false)
                                    }}
                                >
                                    {t('search|Visual search choose gallery', {
                                        defaultValue: 'Elegir de la galería'
                                    })}
                                </button>
                                <button
                                    type="button"
                                    className={s['visual-source-cancel']}
                                    onClick={() => setAndroidSourceOpen(false)}
                                >
                                    {t('search|Visual search source cancel', {
                                        defaultValue: 'Cancelar'
                                    })}
                                </button>
                            </div>
                        </div>,
                        document.body
                    )}
                {isAutocompleteVisible && (
                    <div
                        ref={autocompleteRef}
                        className={s['autocomplete-wrapper']}
                    >
                        <SearchAutocomplete
                            searchTerm={debouncedSearchTerm}
                            searchPageURL={searchPageURL}
                            closeAutocomplete={closeAutocomplete}
                            onSearchSubmit={handleSearch}
                        />
                    </div>
                )}
            </div>
            {isMobile && supportsUserMedia && (
                <>
                    <button
                        aria-label={t('escanear producto')}
                        className="ml-3 focus:outline-none"
                        onClick={() => setStartToScan(true)}
                    >
                        <Barcode />
                    </button>
                    {startToScan && (
                        <Modal
                            onClose={() => setStartToScan(false)}
                            open={startToScan}
                            className="scanner-modal"
                        >
                            <Scanner
                                onBarcodeDetected={handleBarcodeDetected}
                                startToScan={startToScan}
                            />
                        </Modal>
                    )}
                </>
            )}
        </div>
    )
}

export default Searchbar
